/**
 * Storage utility for web (localStorage wrapper)
 * Provides async-like interface similar to React Native AsyncStorage
 */

class Storage {
    /**
     * Get a value from localStorage
     * @param key - The key to retrieve
     * @returns Promise resolving to the value or null if not found
     */
    static async get(key: string): Promise<string | null> {
        try {
            const value = localStorage.getItem(key);
            return value;
        } catch (error) {
            console.error(`[Storage] Error getting ${key}:`, error);
            return null;
        }
    }

    /**
     * Set a value in localStorage
     * @param key - The key to set
     * @param value - The value to store
     * @returns Promise resolving when complete
     */
    static async set(key: string, value: string): Promise<void> {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            console.error(`[Storage] Error setting ${key}:`, error);
            throw error;
        }
    }

    /**
     * Remove a value from localStorage
     * @param key - The key to remove
     * @returns Promise resolving when complete
     */
    static async remove(key: string): Promise<void> {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error(`[Storage] Error removing ${key}:`, error);
            throw error;
        }
    }

    /**
     * Clear all items from localStorage
     * @returns Promise resolving when complete
     */
    static async clear(): Promise<void> {
        try {
            localStorage.clear();
        } catch (error) {
            console.error('[Storage] Error clearing storage:', error);
            throw error;
        }
    }

    /**
     * Get all keys from localStorage
     * @returns Promise resolving to array of keys
     */
    static async getAllKeys(): Promise<string[]> {
        try {
            return Object.keys(localStorage);
        } catch (error) {
            console.error('[Storage] Error getting all keys:', error);
            return [];
        }
    }
}

export default Storage;
